import { Settings, X, Minimize2, Maximize2 } from 'lucide-react'

export default function ICTQuantumBadge({
  result,
  onOpenSettings,
  onClose,
  onMinimize,
  minimized = false,
  version = '1.0',
  onHeaderMouseDown,
}) {
  const data = result || {}
  const signal = data.signal || {}
  const dashboard = data.dashboard || {}
  const htf = data.htf_bias || { direction: 'neutral', strength: 0, timeframe: '' }
  const pd = typeof data.pd_position === 'number' ? data.pd_position : 0.5

  const lines = data.lines || []
  const lastStructure = lines.length > 0
    ? lines.filter(l => l.type === 'BOS' || l.type === 'CHoCH').slice(-1)[0]
    : null
  const trend = dashboard.swing_trend || data.current_trend || 'neutral'
  const trendColor = trend === 'bull' ? '#089981' : trend === 'bear' ? '#f23645' : '#B08D57'

  const activeObs = dashboard.active_obs ?? 0
  const activeFvgs = dashboard.active_fvgs ?? 0
  const activeIfvgs = dashboard.active_ifvgs ?? 0

  const hasSignal = signal && signal.direction && signal.direction !== 'none'
  const signalColor = hasSignal ? (signal.direction === 'long' ? '#089981' : '#f23645') : '#64748b'

  return (
    <div
      className="absolute top-2 left-2 z-10 flex flex-col gap-1 rounded-lg backdrop-blur-sm shadow-lg overflow-hidden min-w-[220px]"
      style={{ background: 'var(--paper, #FDFDFA)', border: '1px solid var(--hair, rgba(176,141,87,0.35))' }}
    >
      <div
        className="flex items-center gap-1.5 px-2.5 pt-2 pb-1 cursor-grab active:cursor-grabbing"
        onMouseDown={onHeaderMouseDown}
      >
        <span
          className="text-[11px] font-semibold tracking-tight"
          style={{ fontFamily: "'Playfair Display', Georgia, serif", color: 'var(--green-deep, #0B3D2E)' }}
        >
          ICT / SMC Quantum <span className="opacity-60 font-normal">[{version}]</span>
          {minimized && (
            <span className="ml-1.5 text-[10px]" style={{ color: trendColor }}>· {trend.toUpperCase()}</span>
          )}
        </span>
        <button onClick={onOpenSettings} title="Configuración" className="ml-auto p-0.5 transition-colors rounded hover:bg-black/5" style={{ color: 'var(--ink, #15211B)' }}>
          <Settings size={12} />
        </button>
        {minimized ? (
          <button onClick={onMinimize} title="Expandir" className="p-0.5 transition-colors rounded hover:bg-black/5" style={{ color: 'var(--ink, #15211B)' }}>
            <Maximize2 size={12} />
          </button>
        ) : (
          <button onClick={onMinimize} title="Minimizar" className="p-0.5 transition-colors rounded hover:bg-black/5" style={{ color: 'var(--ink, #15211B)' }}>
            <Minimize2 size={12} />
          </button>
        )}
        <button onClick={onClose} title="Ocultar dashboard" className="p-0.5 transition-colors rounded hover:bg-black/5" style={{ color: 'var(--ink, #15211B)' }}>
          <X size={12} />
        </button>
      </div>

      {!minimized && (
        <>
          <div className="px-2.5 pb-1">
            <div className="flex items-center justify-between">
              <span className="text-[10px]" style={{ color: 'var(--ink, #15211B)' }}>Sesgo estructural</span>
              <span className="text-[11px] font-semibold" style={{ color: trendColor }}>{trend.toUpperCase()}</span>
            </div>
          </div>

          <div className="flex flex-col gap-0.5 px-2.5 pb-1">
            <CountBar label="OBs" value={activeObs} color={trendColor} />
            <CountBar label="FVG" value={activeFvgs} color={trendColor} />
            <CountBar label="iFVG" value={activeIfvgs} color={trendColor} />
          </div>

          {lastStructure && (
            <div className="px-2.5 pb-1" style={{ borderTop: '1px solid var(--hair, rgba(176,141,87,0.35))' }}>
              <div className="pt-1 flex items-center justify-between">
                <span className="text-[10px]" style={{ color: 'var(--ink, #15211B)' }}>Último evento</span>
                <span className="text-[10px] font-medium" style={{ color: lastStructure.direction === 'bull' ? '#089981' : '#f23645' }}>
                  {lastStructure.type} {lastStructure.direction.toUpperCase()}
                </span>
              </div>
            </div>
          )}

          {htf.direction !== 'neutral' && htf.timeframe && (
            <div className="px-2.5 pb-1" style={{ borderTop: '1px solid var(--hair, rgba(176,141,87,0.35))' }}>
              <div className="pt-1 flex items-center justify-between">
                <span className="text-[10px]" style={{ color: 'var(--ink, #15211B)' }}>HTF {htf.timeframe.toUpperCase()}</span>
                <span className="text-[10px] font-medium" style={{ color: htf.direction === 'bull' ? '#089981' : '#f23645' }}>
                  {htf.direction.toUpperCase()} {Math.round((htf.strength || 0) * 100)}%
                </span>
              </div>
            </div>
          )}

          <div className="px-2.5 pb-1" style={{ borderTop: '1px solid var(--hair, rgba(176,141,87,0.35))' }}>
            <div className="pt-1 flex items-center justify-between">
              <span className="text-[10px]" style={{ color: 'var(--ink, #15211B)' }}>PD array</span>
              <span className="text-[10px] font-medium" style={{ color: pd > 0.6 ? '#f23645' : pd < 0.4 ? '#089981' : '#64748b' }}>
                {pd.toFixed(2)}
              </span>
            </div>
          </div>

          {hasSignal && (
            <div className="px-2.5 pb-2" style={{ borderTop: '1px solid var(--hair, rgba(176,141,87,0.35))' }}>
              <div className="pt-1.5 pb-1">
                <span className="text-[10px] font-semibold" style={{ color: signalColor }}>
                  {signal.direction === 'long' ? 'LONG' : 'SHORT'} {signal.grade || ''}
                </span>
              </div>
              <div className="grid grid-cols-2 gap-x-2 gap-y-1">
                <div className="flex justify-between">
                  <span className="text-[9px]" style={{ color: 'var(--ink-soft, #4A5A52)' }}>Entry</span>
                  <span className="text-[9px] font-medium" style={{ color: 'var(--ink, #15211B)' }}>{signal.entry_price?.toLocaleString('es-ES', { maximumFractionDigits: 2 })}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[9px]" style={{ color: 'var(--ink-soft, #4A5A52)' }}>SL</span>
                  <span className="text-[9px] font-medium" style={{ color: '#f23645' }}>{signal.stop_loss?.toLocaleString('es-ES', { maximumFractionDigits: 2 })}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[9px]" style={{ color: 'var(--ink-soft, #4A5A52)' }}>TP1</span>
                  <span className="text-[9px] font-medium" style={{ color: '#089981' }}>{signal.take_profit_1?.toLocaleString('es-ES', { maximumFractionDigits: 2 })}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[9px]" style={{ color: 'var(--ink-soft, #4A5A52)' }}>R:R</span>
                  <span className="text-[9px] font-medium" style={{ color: 'var(--ink, #15211B)' }}>{signal.risk_reward_1?.toFixed(2)}R</span>
                </div>
                <div className="flex justify-between col-span-2">
                  <span className="text-[9px]" style={{ color: 'var(--ink-soft, #4A5A52)' }}>Confluencia</span>
                  <span className="text-[9px] font-medium" style={{ color: signalColor }}>{signal.confluence_score}/9 · {signal.strength || 'medium'}</span>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}

function CountBar({ label, value, color }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-[10px]" style={{ color: 'var(--ink, #15211B)' }}>{label}</span>
      <span className="text-[10px] font-medium tabular-nums" style={{ color }}>{value}</span>
    </div>
  )
}
